import type { EmailSettings, UpdateEmailSettingsInput } from '@/api/types';

/** Local, editable copy of the email settings. Secrets are always blank unless just typed. */
export interface EmailDraft {
  inboundAddress: string;
  fromName: string;
  fromAddress: string;
  replyTo: string;
  signature: string;
  transport: EmailSettings['transport'];
  relayUrl: string;
  relayAuthHeader: string;
  smtpHost: string;
  smtpPort: string;
  smtpSecure: boolean;
  smtpUsername: string;
  autoAckEnabled: boolean;
  autoAckBody: string;
  relayAuthValue: string;
  smtpPassword: string;
}

export interface EmailSecretsDraft {
  clearRelayAuthValue: boolean;
  clearSmtpPassword: boolean;
}

export function draftFromSettings(s: EmailSettings): EmailDraft {
  return {
    inboundAddress: s.inboundAddress,
    fromName: s.fromName,
    fromAddress: s.fromAddress,
    replyTo: s.replyTo ?? '',
    signature: s.signature,
    transport: s.transport,
    relayUrl: s.relayUrl,
    relayAuthHeader: s.relayAuthHeader ?? '',
    smtpHost: s.smtpHost,
    smtpPort: String(s.smtpPort),
    smtpSecure: s.smtpSecure,
    smtpUsername: s.smtpUsername,
    autoAckEnabled: s.autoAckEnabled,
    autoAckBody: s.autoAckBody,
    relayAuthValue: '',
    smtpPassword: '',
  };
}

/**
 * Only the fields that actually changed. Untouched secrets are omitted entirely (the backend keeps
 * the stored value); a blank field with its clear flag sends null; a typed value replaces it.
 */
export function buildEmailPatch(
  draft: EmailDraft,
  secrets: EmailSecretsDraft,
  s: EmailSettings,
): UpdateEmailSettingsInput {
  const patch: UpdateEmailSettingsInput = {};
  const inboundAddress = draft.inboundAddress.trim().toLowerCase();
  const fromAddress = draft.fromAddress.trim().toLowerCase();
  if (inboundAddress !== s.inboundAddress) patch.inboundAddress = inboundAddress;
  if (fromAddress !== s.fromAddress) patch.fromAddress = fromAddress;
  if (draft.fromName.trim() !== s.fromName) patch.fromName = draft.fromName.trim();
  if (draft.signature !== s.signature) patch.signature = draft.signature;
  const replyTo = draft.replyTo.trim().toLowerCase();
  if (replyTo !== (s.replyTo ?? '')) patch.replyTo = replyTo || null;
  if (draft.transport !== s.transport) patch.transport = draft.transport;

  if (draft.relayUrl.trim() !== s.relayUrl) patch.relayUrl = draft.relayUrl.trim();
  const relayAuthHeader = draft.relayAuthHeader.trim();
  if (relayAuthHeader !== (s.relayAuthHeader ?? '')) patch.relayAuthHeader = relayAuthHeader || null;
  if (draft.relayAuthValue) patch.relayAuthValue = draft.relayAuthValue;
  else if (secrets.clearRelayAuthValue && s.hasRelayAuthValue) patch.relayAuthValue = null;

  if (draft.smtpHost.trim() !== s.smtpHost) patch.smtpHost = draft.smtpHost.trim();
  const smtpPort = Number(draft.smtpPort);
  if (Number.isInteger(smtpPort) && smtpPort >= 1 && smtpPort <= 65535 && smtpPort !== s.smtpPort) {
    patch.smtpPort = smtpPort;
  }
  if (draft.smtpSecure !== s.smtpSecure) patch.smtpSecure = draft.smtpSecure;
  if (draft.smtpUsername.trim() !== s.smtpUsername) patch.smtpUsername = draft.smtpUsername.trim();
  if (draft.smtpPassword) patch.smtpPassword = draft.smtpPassword;
  else if (secrets.clearSmtpPassword && s.hasSmtpPassword) patch.smtpPassword = null;

  if (draft.autoAckEnabled !== s.autoAckEnabled) patch.autoAckEnabled = draft.autoAckEnabled;
  if (draft.autoAckBody !== s.autoAckBody) patch.autoAckBody = draft.autoAckBody;

  return patch;
}
